import React, { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Button, Form, Message, Modal } from 'semantic';
import { Formik } from 'formik';
import { concat, size } from 'lodash-es';
import { useMutation } from '@tanstack/react-query';

import WrappedModal from 'components/modal/WrappedModal';
import {
  ChargingGroupFormFields,
  ChargingGroupFormValues,
} from './ChargingGroupFormFields';
import { ConnectorsSelection, getConnectorSelectionKey } from './utils';
import {
  ChargingGroup,
  ChargingGroupConnectorFilter,
  ChargingGroupFilter,
} from 'subdomains/cpo/types/charging-policy';
import { validationSchema } from './ChargingGroupFormFields';
import { FormikErrorsMessage } from 'subdomains/cpo/components/FormikErrorsMessage';
import { Optional } from 'subdomains/cpo/types/utility';
import { request } from 'utils/api';
import { CHARGING_GROUPS_V1_API_URL } from 'subdomains/cpo/router/api';
import { useToast } from 'components/Toast/useToast';
import { formatAPIError } from 'utils/error';

type Props = {
  trigger: React.ReactNode;
  mode: 'create' | 'edit';
  chargingGroup?: ChargingGroup;
  onSuccess?: () => void;
};

export function ChargingGroupModal({
  chargingGroup,
  trigger,
  mode,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  let initialValues: ChargingGroupFormValues = {
    label: '',
    description: '',
    connectorsSelection: {},
  };
  if (chargingGroup) {
    const connectorsSelection = chargingGroupFiltersToConnectorsSelection(
      chargingGroup.filters
    );

    initialValues = {
      ...initialValues,
      label: chargingGroup.label,
      description: chargingGroup.description ?? '',
      connectorsSelection: connectorsSelection,
    };
  }

  const mutation = useMutation({
    mutationFn: (values: ChargingGroupFormValues) => {
      const connectors: ChargingGroupConnectorFilter[] = Object.values(
        values.connectorsSelection
      ).map((connector) => ({
        chargingStationId: connector.opChargingStationId,
        evseId: connector.ocppEvseId,
        connectorId: connector.ocppConnectorId,
      }));

      const filters = chargingGroup?.filters ?? [];

      // we remove the filters that have connectors
      // to guarantee that we don't leave any leftover connectors on charging group
      // then we add the new connectors to the filters
      const filtersWithoutConnectors = concat(
        filters.filter((filter) => size(filter.connectors) <= 0),
        {
          connectors,
          accountIds: [],
          locationIds: [],
        }
      );

      const body: Optional<
        ChargingGroup,
        'id' | 'providerId' | 'updatedAt' | 'createdAt'
      > = {
        id: chargingGroup?.id,
        label: values.label,
        description: values.description,
        filters: filtersWithoutConnectors,
      };

      if (mode === 'edit') {
        return request({
          method: 'PUT',
          path: `${CHARGING_GROUPS_V1_API_URL}/${chargingGroup?.id}`,
          body,
        });
      }
      return request({
        method: 'POST',
        path: CHARGING_GROUPS_V1_API_URL,
        body,
      });
    },
    onSuccess: () => {
      setIsOpen(false);
      if (mode === 'create') {
        toast.success(
          t(
            'providerAdmin.chargingGroupForm.createSuccess',
            'Charging group created successfully'
          )
        );
      } else {
        toast.success(
          t(
            'providerAdmin.chargingGroupForm.updateSuccess',
            'Charging group updated successfully'
          )
        );
      }
      onSuccess?.();
    },
    onError: (e) => {
      setError(formatAPIError(e));
    },
  });

  const submit = useCallback(
    async (values: ChargingGroupFormValues) => {
      setError(null);
      mutation.mutate(values);
    },
    [mutation]
  );

  return (
    <WrappedModal isOpen={isOpen} setIsOpen={setIsOpen} trigger={trigger}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onReset={() => setIsOpen(false)}
        onSubmit={submit}>
        {({
          handleSubmit,
          handleReset,
          values,
          setFieldValue,
          errors,
          submitCount,
        }) => (
          <>
            <Modal.Header>
              {mode === 'create'
                ? t(
                    'providerAdmin.chargingGroupForm.createChargingGroupTitle',
                    'New Charging Group'
                  )
                : t(
                    'providerAdmin.chargingGroupForm.editChargingGroupTitle',
                    'Edit Charging Group'
                  )}
            </Modal.Header>
            <Modal.Content>
              <Form>
                <ChargingGroupFormFields
                  values={values}
                  setFieldValue={setFieldValue}
                />
              </Form>
              {submitCount > 0 && <FormikErrorsMessage errors={errors} />}
              {error && <Message error>{error}</Message>}
            </Modal.Content>
            <Modal.Actions>
              <Button
                content={t(
                  'providerAdmin.chargingGroupForm.cancelButtonText',
                  'Cancel'
                )}
                as="button"
                basic
                onClick={handleReset}
              />
              <Button
                content={
                  mode === 'create'
                    ? t(
                        'providerAdmin.chargingGroupForm.createButtonText',
                        'Create Charging Group'
                      )
                    : t(
                        'providerAdmin.chargingGroupForm.updateButtonText',
                        'Update Charging Group'
                      )
                }
                as="button"
                style={{ marginLeft: '0' }}
                onClick={handleSubmit}
              />
            </Modal.Actions>
          </>
        )}
      </Formik>
    </WrappedModal>
  );
}

function chargingGroupFiltersToConnectorsSelection(
  filters: ChargingGroupFilter[]
): ConnectorsSelection {
  return filters
    .flatMap((filter) => filter.connectors)
    .reduce((acc, connector) => {
      const key = getConnectorSelectionKey({
        opChargingStationId: connector.chargingStationId,
        ocppEvseId: connector.evseId,
        ocppConnectorId: connector.connectorId,
      });
      acc[key] = {
        opChargingStationId: connector.chargingStationId,
        ocppEvseId: connector.evseId,
        ocppConnectorId: connector.connectorId,
      };
      return acc;
    }, {} as ConnectorsSelection);
}
